// Configuration API
const API_BASE_URL = import.meta.env.VITE_REACT_APP_API_EP_URI;

interface UserInfosResponse {
  id: number;
  login: string;
  lastname: string;
  firstname: string;
  email: string;
  access: string;
  td_group: string;
  project_group: ProjectGroupInfos;
}

interface ProjectGroupInfos {
    id: number;
    generic_name: string;
    name: string;
    members: {
        user_id: number;
        lastname: string;
        firstname: string;
        email: string;
    }[];
}

interface CycleResult {
  id: number;
  project_group_id: number;
  cycle_id: number;
  date: string;
  lint_errors: number;
  mypy_errors: number;
  isort_errors: number;
  git_diff_score_first_commit: number;
  git_diff_score_previous_commit: number;
  html_errors: number;
  css_errors: number;
  quality_code_score: string;
  maintainability_score: string;
  security_score?: string | null;
  us_e2e_tests: Record<string, number>;
  global_score: number;
}

// Service de base avec gestion d'erreurs
export interface CycleResultsFilters {
  page?: number;
  perPage?: number;
  tdGroup?: string;
  projectGroupId?: number;
  member?: string;
  latestOnly?: boolean;
}

export interface CycleResultsPage {
  results: (CycleResult & { is_latest_of_group: boolean; is_latest_of_branch: boolean; branch?: string | null })[];
  pagination: { total: number; page: number; per_page: number; pages: number; has_next: boolean; has_prev: boolean };
}

class UserService {

  // Récupérer les informations utilisateur
  async getUserInfos(id: number, accessToken: string): Promise<UserInfosResponse> {
    const response = await fetch(`${API_BASE_URL}/users/${id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const json = await response.json();
    return json.data;
  }

  // Récupérer les paramètres de configuration du cours
    async getCourseConfig(accessToken: string): Promise<any> {
        const response = await fetch(`${API_BASE_URL}/course_config`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`
            },
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return response.json();
    }

  // Récupérer les résultats des cycles pour l'utilisateur
    // Une page de résultats d'UN cycle, filtrée et paginée par le serveur (refonte Charts : plus de
    // téléchargement de tout l'historique). Scores et points sont ceux de ce cycle (sous-cycles).
    async getCycleResultsPage(cycleId: number | string, filters: CycleResultsFilters, accessToken: string,
                              signal?: AbortSignal): Promise<CycleResultsPage> {
        const query = new URLSearchParams({ cycle_id: String(cycleId), page: String(filters.page || 1),
                                            per_page: String(filters.perPage || 25) });
        if (filters.tdGroup) query.set('td_group', filters.tdGroup);
        if (filters.projectGroupId) query.set('project_group_id', String(filters.projectGroupId));
        if (filters.member) query.set('member', filters.member);
        if (filters.latestOnly) query.set('latest_only', '1');
        const response = await fetch(`${API_BASE_URL}/cycle_results?${query.toString()}`, {
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${accessToken}` },
            signal,
        });
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
    }

  // Récupérer les détails d'un résultat de cycle spécifique (avec rapports)
    async getCycleResultDetails(resultId: number, accessToken: string): Promise<any> {
        const response = await fetch(`${API_BASE_URL}/cycle_results/${resultId}`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`
        },
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return response.json();
    }

  // Supprimer une trace de cycle (admin ou membre du groupe projet)
    async deleteCycleResult(resultId: number, accessToken: string): Promise<void> {
        const response = await fetch(`${API_BASE_URL}/cycle_results/${resultId}`, {
        method: 'DELETE',
        headers: {
            'Authorization': `Bearer ${accessToken}`
        },
        });

        if (!response.ok) {
            const body = await response.json().catch(() => ({}));
            throw new Error(body.message || `HTTP error! status: ${response.status}`);
        }
    }

  // Vérifier l'existence d'un rapport ZIP pour un groupe
    async checkReportZipExists(groupId: number, branch: string, accessToken: string): Promise<boolean> {
        const response = await fetch(`${API_BASE_URL}/project-groups/${groupId}/report-zip?branch=${branch}`, {
            method: 'HEAD',
            headers: {
                'Authorization': `Bearer ${accessToken}`
            },
        });

        return response.ok;
    }

  // Télécharger le rapport ZIP pour un groupe
    async downloadReportZip(groupId: number, branch: string, accessToken: string): Promise<void> {
        const response = await fetch(`${API_BASE_URL}/project-groups/${groupId}/report-zip?branch=${branch}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${accessToken}`
            },
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        // Récupérer le nom du fichier depuis l'en-tête Content-Disposition
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = `report_${branch}.zip`;
        if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
            if (filenameMatch) {
                filename = filenameMatch[1];
            }
        }

        // Télécharger le fichier
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
    }

  // Télécharger le classeur Excel des notes (enseignant·es et admin uniquement)
    async downloadNotesExcel(accessToken: string): Promise<void> {
        const response = await fetch(`${API_BASE_URL}/notes-excel`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${accessToken}`
            },
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = 'notes_projets.xlsx';
        if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
            if (filenameMatch) {
                filename = filenameMatch[1];
            }
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
    }

  // Récupérer tous les groupes projets avec leurs membres
  // OPTIMISATION: Endpoint séparé pour réduire la taille des réponses de GET /cycle_results
    async getProjectGroups(accessToken: string): Promise<any[]> {
        const response = await fetch(`${API_BASE_URL}/project-groups`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`
            },
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return response.json();
    }

}

export const userService = new UserService();